import { useEffect, useRef, useState, useCallback } from "react";
import { AppToolbarButton } from "./WhatsNew";
import Map from "ol/Map";
import View from "ol/View";
import { fromLonLat, toLonLat } from "ol/proj";
import { Feature } from "ol";
import { Point, LineString } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Circle as CircleStyle, Fill, Stroke, Style, Icon } from "ol/style";
import Geolocation from "ol/Geolocation";
import Draw from "ol/interaction/Draw";
import Translate from "ol/interaction/Translate";
import { unByKey } from "ol/Observable";
import Collection from "ol/Collection";
import { getCenter } from "ol/extent";
import "ol/ol.css";
import { projectCoordsToLonLat, createBaseLayers } from "./mapUtils";
import { useProject } from "./ProjectContext";
import { storageKey } from "./projectConfig";

/* ── Sidebar styles (matching 3D Survey view, dark variant) ── */

const HUD_TEXT = "#f7f8fb";
const NGI_STEEL = "#75787b";

const SIDEBAR_DARK_BG = "linear-gradient(180deg, rgba(0,0,0,0.82), rgba(0,0,0,0.72))";

function sidebarIconBadge(activeColor?: string): React.CSSProperties {
  return {
    width: 28, height: 28, borderRadius: 10,
    display: "grid", placeItems: "center",
    background: activeColor ? activeColor + "26" : "rgba(255,255,255,0.08)",
    border: `1px solid ${activeColor ? activeColor + "44" : "rgba(255,255,255,0.12)"}`,
    color: "#f6f8fb", fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.04em", flexShrink: 0,
  };
}

function sidebarBtnStyle(active: boolean, activeColor?: string): React.CSSProperties {
  const c = activeColor ?? "#002855";
  return {
    minHeight: 34, display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 8, width: "100%",
    background: active ? c + "24" : "rgba(255,255,255,0.06)",
    color: active ? "#f6f8fb" : "rgba(247,248,251,0.84)",
    border: `1px solid ${active ? c + "66" : "rgba(255,255,255,0.1)"}`,
    borderRadius: 10, cursor: "pointer", padding: "0.38rem 0.5rem", textAlign: "left" as const,
    boxShadow: active ? `0 0 0 1px ${c}22 inset` : "none",
    transition: "all 0.15s ease",
  };
}

const sidebarCardStyle: React.CSSProperties = {
  pointerEvents: "auto", padding: "0.35rem", borderRadius: 16,
  background: SIDEBAR_DARK_BG,
  border: `1px solid ${PANEL_BORDER}`,
  backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
  boxShadow: "0 12px 36px rgba(0,0,0,0.4)",
};

const sidebarGridStyle: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr", gap: "0.35rem", padding: "0 0.15rem 0.15rem",
};

function MapSidebarItem({ icon, title, subtitle, active, activeColor, onClick, onZoom }: {
  icon: string; title: string; subtitle: string; active: boolean; activeColor?: string; onClick: () => void; onZoom?: () => void;
}) {
  return (
    <button type="button" onClick={onClick} style={sidebarBtnStyle(active, activeColor)}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
        <span aria-hidden="true" style={sidebarIconBadge(activeColor)}>{icon}</span>
        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <span style={{ fontSize: "0.74rem", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
          <span style={{ fontSize: "0.62rem", opacity: 0.62, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitle}</span>
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
        {onZoom && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onZoom(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onZoom(); } }}
            title="Zoom to extent"
            style={{
              width: 28, height: 28, borderRadius: 8,
              display: "grid", placeItems: "center",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              cursor: "pointer",
              color: "rgba(247,248,251,0.7)",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              {/* 4-way arrow */}
              <line x1="12" y1="3" x2="12" y2="21" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <polyline points="8 7 12 3 16 7" />
              <polyline points="16 17 12 21 8 17" />
              <polyline points="7 8 3 12 7 16" />
              <polyline points="17 16 21 12 17 8" />
            </svg>
          </span>
        )}
        <span style={{
          flexShrink: 0, fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
          padding: "0.18rem 0.4rem", borderRadius: 999,
          background: active ? (activeColor ?? "#002855") + "2b" : "rgba(255,255,255,0.08)",
          border: `1px solid ${active ? (activeColor ?? "#002855") + "4a" : "rgba(255,255,255,0.1)"}`,
          color: active ? "#f6f8fb" : "rgba(247,248,251,0.6)",
        }}>
          {active ? "On" : "Off"}
        </span>
      </div>
    </button>
  );
}

/* ── Types ────────────────────────────────────────────────────── */

type ToolType = "dipdir" | "weaknesszone" | "rockclass";

/** Base64-encoded photo with optional caption */
interface PhotoAttachment {
  id: string;
  dataUrl: string; // base64 data URL
  caption?: string;
  timestamp: number;
}

interface DipDirObservation {
  id: string;
  type: "dipdir";
  lon: number;
  lat: number;
  dip: number;
  dipDirection: number;
  notes: string;
  photos?: PhotoAttachment[];
  timestamp: number;
}

interface WeaknessZoneObservation {
  id: string;
  type: "weaknesszone";
  /** Polyline as array of [lon, lat] pairs */
  coords: [number, number][];
  dip?: number;
  dipDirection?: number;
  thickness?: number;
  notes: string;
  photos?: PhotoAttachment[];
  timestamp: number;
}

interface RockClassObservation {
  id: string;
  type: "rockclass";
  lon: number;
  lat: number;
  rockType?: string;
  /** Rock Quality Designation (0-100%) */
  rqd?: number;
  /** RMR sub-parameters (Bieniawski 1989) – ratings that sum to RMR */
  rmrUcs?: number;       // Uniaxial compressive strength rating 0-15
  rmrRqd?: number;       // RQD rating 0-20
  rmrSpacing?: number;   // Discontinuity spacing rating 0-20
  rmrCondition?: number; // Condition of discontinuities rating 0-30
  rmrWater?: number;     // Groundwater rating 0-15
  rmrOrientation?: number; // Orientation adjustment -60 to 0
  /** Computed RMR total (0-100) */
  rmr?: number;
  /** Q-system sub-parameters (Barton et al.) */
  qRqd?: number;   // RQD used in Q calc 0-100
  qJn?: number;    // Joint set number 0.5-20
  qJr?: number;    // Joint roughness number 0.5-4
  qJa?: number;    // Joint alteration number 0.75-20
  qJw?: number;    // Joint water reduction 0.05-1
  qSrf?: number;   // Stress reduction factor 0.5-400
  /** Computed Q-value */
  qValue?: number;
  notes: string;
  photos?: PhotoAttachment[];
  timestamp: number;
}

type Observation = DipDirObservation | WeaknessZoneObservation | RockClassObservation;

const LEGACY_OBS_KEY = "e16-gis:observations";

function obsKey(projectId: string): string {
  return storageKey(projectId, "gis-observations");
}

function loadObservations(projectId: string): Observation[] {
  try {
    const key = obsKey(projectId);
    // Migrate legacy data
    if (!localStorage.getItem(key)) {
      const legacy = localStorage.getItem(LEGACY_OBS_KEY);
      if (legacy) localStorage.setItem(key, legacy);
    }
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function persistObservations(projectId: string, obs: Observation[]) {
  localStorage.setItem(obsKey(projectId), JSON.stringify(obs));
}

/* ── Strike/dip symbol as SVG data URL ────────────────────────── */

function dipDirIconSvg(dip: number, dipDir: number): string {
  const tickLen = 6 + (dip / 90) * 14;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <g transform="rotate(${dipDir}, 20, 20)">
      <line x1="4" y1="20" x2="36" y2="20" stroke="#222" stroke-width="2.5" stroke-linecap="round"/>
      <line x1="20" y1="20" x2="20" y2="${20 - tickLen}" stroke="#222" stroke-width="2" stroke-linecap="round"/>
    </g>
    <text x="20" y="38" text-anchor="middle" font-size="9" font-weight="700" fill="#222" font-family="system-ui">${dip}</text>
  </svg>`;
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

/* ── Weakness zone line style ─────────────────────────────────── */

const weaknessZoneStyle = new Style({
  stroke: new Stroke({
    color: "#d32f2f",
    width: 3,
    lineDash: [10, 6],
  }),
});

const weaknessZoneDrawStyle = new Style({
  stroke: new Stroke({
    color: "rgba(211,47,47,0.6)",
    width: 2.5,
    lineDash: [8, 5],
  }),
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: "rgba(211,47,47,0.4)" }),
    stroke: new Stroke({ color: "#d32f2f", width: 1.5 }),
  }),
});

/* ── Rock classification map icon ──────────────────────────────── */

function rockClassIconSvg(): string {
  // Standard geological rock outcrop symbol: double inverted-V chevron
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <polyline points="8,18 16,8 24,18" fill="none" stroke="#3e2723" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    <polyline points="8,26 16,16 24,26" fill="none" stroke="#3e2723" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

/* ── Photo capture helper ─────────────────────────────────────── */

// We keep a single persistent file-input in the DOM so the browser
// maintains the reference while the camera app is open (avoids GC /
// page-lifecycle issues on mobile that reset React state).
let _photoInput: HTMLInputElement | null = null;
let _photoCallback: ((p: PhotoAttachment | null) => void) | null = null;

function getPhotoInput(): HTMLInputElement {
  if (_photoInput) return _photoInput;
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.capture = "environment";
  input.style.display = "none";
  document.body.appendChild(input);

  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (!file) { _photoCallback?.(null); _photoCallback = null; return; }
    const reader = new FileReader();
    reader.onload = () => {
      _photoCallback?.({
        id: crypto.randomUUID(),
        dataUrl: reader.result as string,
        timestamp: Date.now(),
      });
      _photoCallback = null;
    };
    reader.onerror = () => { _photoCallback?.(null); _photoCallback = null; };
    reader.readAsDataURL(file);
    // Reset so the same file can be picked again
    input.value = "";
  });

  _photoInput = input;
  return input;
}

function capturePhoto(callback: (p: PhotoAttachment | null) => void) {
  _photoCallback = callback;
  getPhotoInput().click();
}

/* ── Node styles for vertex editing ───────────────────────────── */

const nodeStyle = new Style({
  image: new CircleStyle({
    radius: 10,
    fill: new Fill({ color: "#d32f2f" }),
    stroke: new Stroke({ color: "#fff", width: 2.5 }),
  }),
});

const nodeDeleteStyle = new Style({
  image: new CircleStyle({
    radius: 10,
    fill: new Fill({ color: "#ff8a80" }),
    stroke: new Stroke({ color: "#fff", width: 2.5 }),
  }),
});

/* ── Styles ───────────────────────────────────────────────────── */

const GLASS_PANEL = "linear-gradient(180deg, rgba(0,0,0,0.78), rgba(0,0,0,0.68))";
const PANEL_BORDER = "rgba(255,255,255,0.12)";
const BLUR = "blur(14px)";
const GLASS_SHADOW = "0 8px 24px rgba(0,0,0,0.4)";

const panelBase: React.CSSProperties = {
  background: GLASS_PANEL,
  border: `1px solid ${PANEL_BORDER}`,
  backdropFilter: BLUR,
  WebkitBackdropFilter: BLUR,
  borderRadius: 14,
  color: "#f7f8fb",
  boxShadow: GLASS_SHADOW,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: `1px solid rgba(255,255,255,0.12)`,
  background: "rgba(255,255,255,0.06)",
  color: "#f7f8fb",
  fontSize: "0.85rem",
  fontFamily: "system-ui, sans-serif",
  outline: "none",
};

/* ── Q-system parameter lookup tables (Barton et al. 1974/2002) ── */

const Q_JN_OPTIONS: { value: number; label: string }[] = [
  { value: 0.5, label: "0.5 — Massive, no or few joints" },
  { value: 1, label: "1 — One joint set" },
  { value: 2, label: "2 — One joint set + random" },
  { value: 3, label: "3 — Two joint sets" },
  { value: 4, label: "4 — Two joint sets + random" },
  { value: 6, label: "6 — Three joint sets" },
  { value: 9, label: "9 — Three joint sets + random" },
  { value: 12, label: "12 — Four or more joint sets" },
  { value: 15, label: "15 — Crushed rock, earthlike" },
  { value: 20, label: "20 — Crushed rock, earthlike (severe)" },
];

const Q_JR_OPTIONS: { value: number; label: string }[] = [
  { value: 0.5, label: "0.5 — Slickensided, planar" },
  { value: 1, label: "1 — Smooth, planar" },
  { value: 1.5, label: "1.5 — Rough or irregular, planar" },
  { value: 2, label: "2 — Smooth, undulating" },
  { value: 3, label: "3 — Rough or irregular, undulating" },
  { value: 4, label: "4 — Discontinuous joints" },
];

const Q_JA_OPTIONS: { value: number; label: string }[] = [
  { value: 0.75, label: "0.75 — Tightly healed, hard, non-softening" },
  { value: 1, label: "1 — Unaltered joint walls, surface staining only" },
  { value: 2, label: "2 — Slightly altered, non-softening mineral coatings" },
  { value: 3, label: "3 — Silty- or sandy-clay coatings, small clay fraction" },
  { value: 4, label: "4 — Softening or low-friction clay mineral coatings" },
  { value: 6, label: "6 — Sandy particles, clay-free disintegrated rock" },
  { value: 8, label: "8 — Strongly over-consolidated non-softening clay" },
  { value: 10, label: "10 — Medium or low over-consolidation, softening" },
  { value: 12, label: "12 — Swelling clay, < 5 mm thickness" },
  { value: 13, label: "13 — Zones with clay, disintegrated rock (thin)" },
  { value: 15, label: "15 — Zones with clay, disintegrated rock (thick)" },
  { value: 20, label: "20 — Zones with clay, thick continuous" },
];

const Q_JW_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "1.0 — Dry excavation or minor inflow" },
  { value: 0.66, label: "0.66 — Medium inflow, occasional outwash" },
  { value: 0.5, label: "0.5 — Large inflow, steady pressure" },
  { value: 0.33, label: "0.33 — Large inflow, considerable outwash" },
  { value: 0.2, label: "0.2 — Exceptionally high inflow, decaying with time" },
  { value: 0.1, label: "0.1 — Exceptionally high inflow, no decay" },
  { value: 0.05, label: "0.05 — Exceptionally high inflow, continuous" },
];

const Q_SRF_OPTIONS: { value: number; label: string }[] = [
  { value: 0.5, label: "0.5 — Low stress, near surface, open joints" },
  { value: 1, label: "1 — Medium stress, favourable stress condition" },
  { value: 2.5, label: "2.5 — Heavy rock burst (massive rock)" },
  { value: 5, label: "5 — High stress, very tight structure" },
  { value: 7.5, label: "7.5 — Mild rock burst (massive rock)" },
  { value: 10, label: "10 — Heavy squeezing rock pressure" },
  { value: 15, label: "15 — Mild squeezing rock pressure" },
  { value: 20, label: "20 — Mild swelling rock pressure" },
  { value: 50, label: "50 — Single weakness zone, clay/chemically disintegrated" },
  { value: 100, label: "100 — Multiple weakness zones, loose surrounding rock" },
  { value: 200, label: "200 — Heavy swelling rock pressure" },
  { value: 400, label: "400 — Multiple weakness zones, any depth" },
];

function qClassName(q: number): string {
  if (q >= 400) return "Exceptionally Good";
  if (q >= 100) return "Extremely Good";
  if (q >= 40) return "Very Good";
  if (q >= 10) return "Good";
  if (q >= 4) return "Fair";
  if (q >= 1) return "Poor";
  if (q >= 0.1) return "Very Poor";
  if (q >= 0.01) return "Extremely Poor";
  return "Exceptionally Poor";
}

/* ── RMR parameter lookup tables (Bieniawski 1989) ────────────── */

const RMR_UCS_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "0 — UCS < 1 MPa" },
  { value: 1, label: "1 — 1-5 MPa (very weak)" },
  { value: 2, label: "2 — 5-25 MPa (weak)" },
  { value: 4, label: "4 — 25-50 MPa (medium)" },
  { value: 7, label: "7 — 50-100 MPa (strong)" },
  { value: 12, label: "12 — 100-250 MPa (very strong)" },
  { value: 15, label: "15 — > 250 MPa (extremely strong)" },
];

const RMR_RQD_OPTIONS: { value: number; label: string }[] = [
  { value: 3, label: "3 — RQD < 25% (very poor)" },
  { value: 8, label: "8 — 25-50% (poor)" },
  { value: 13, label: "13 — 50-75% (fair)" },
  { value: 17, label: "17 — 75-90% (good)" },
  { value: 20, label: "20 — 90-100% (excellent)" },
];

const RMR_SPACING_OPTIONS: { value: number; label: string }[] = [
  { value: 5, label: "5 — < 60 mm (very close)" },
  { value: 8, label: "8 — 60-200 mm (close)" },
  { value: 10, label: "10 — 200-600 mm (moderate)" },
  { value: 15, label: "15 — 600-2000 mm (wide)" },
  { value: 20, label: "20 — > 2000 mm (very wide)" },
];

const RMR_CONDITION_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "0 — Soft gouge > 5 mm, continuous" },
  { value: 10, label: "10 — Soft gouge < 5 mm or separation > 5 mm" },
  { value: 20, label: "20 — Slightly rough, separation < 1 mm, slightly weathered" },
  { value: 25, label: "25 — Slightly rough, separation < 1 mm, unweathered" },
  { value: 30, label: "30 — Very rough, unweathered, no separation, tight" },
];

const RMR_WATER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "0 — Flowing (> 125 l/min or p/\u03C3 > 0.5)" },
  { value: 4, label: "4 — Dripping (25-125 l/min or p/\u03C3 0.2-0.5)" },
  { value: 7, label: "7 — Wet (10-25 l/min or p/\u03C3 0.1-0.2)" },
  { value: 10, label: "10 — Damp (< 10 l/min or p/\u03C3 0-0.1)" },
  { value: 15, label: "15 — Completely dry" },
];

const RMR_ORIENTATION_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "0 — Very favourable" },
  { value: -2, label: "-2 — Favourable" },
  { value: -5, label: "-5 — Fair" },
  { value: -10, label: "-10 — Unfavourable" },
  { value: -12, label: "-12 — Very unfavourable (tunnels)" },
  { value: -25, label: "-25 — Very unfavourable (foundations)" },
  { value: -60, label: "-60 — Very unfavourable (slopes)" },
];

function rmrClassName(rmr: number): string {
  if (rmr >= 81) return "Very Good";
  if (rmr >= 61) return "Good";
  if (rmr >= 41) return "Fair";
  if (rmr >= 21) return "Poor";
  return "Very Poor";
}

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.06)",
  color: "#f7f8fb",
  fontSize: "0.78rem",
  fontFamily: "system-ui, sans-serif",
  outline: "none",
  appearance: "auto",
};

/* ── Component ────────────────────────────────────────────────── */

interface MapCollectionProps {
  onBack: () => void;
}

export function MapCollection({ onBack }: MapCollectionProps) {
  const project = useProject();
  const mapRef = useRef<HTMLDivElement>(null);
  const olMapRef = useRef<Map | null>(null);
  const geoRef = useRef<Geolocation | null>(null);
  const obsSourceRef = useRef<VectorSource>(new VectorSource());
  const drawRef = useRef<Draw | null>(null);
  // Node editing for weakness zones
  const nodeSourceRef = useRef<VectorSource>(new VectorSource());
  const nodeLayerRef = useRef<VectorLayer | null>(null);
  const translateRef = useRef<Translate | null>(null);
  const tunnelSourceRef = useRef<VectorSource>(new VectorSource());

  const [isMobile] = useState(() => window.innerWidth < 768);
  const [locError, setLocError] = useState<string | null>(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const tunnelLayerRef = useRef<VectorLayer | null>(null);
  const [showTunnel, setShowTunnel] = useState(true);

  // Tool state
  const [activeTool, setActiveTool] = useState<ToolType>("dipdir");
  const [placing, setPlacing] = useState(false);
  const [placingConfirmed, setPlacingConfirmed] = useState(false);
  // "drawing" = actively drawing a weakness zone line on the map
  const [drawing, setDrawing] = useState(false);
  // Temp geometry from finished draw, waiting for notes before save
  const [drawnCoords, setDrawnCoords] = useState<[number, number][] | null>(null);
  const drawnFeatureRef = useRef<Feature | null>(null);

  // Selection / editing
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editDip, setEditDip] = useState("");
  const [editDipDir, setEditDipDir] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editWzThickness, setEditWzThickness] = useState("");
  // For dipdir repositioning
  const [repositioning, setRepositioning] = useState(false);
  // For weakness zone geometry editing mode
  const [editingGeometry, setEditingGeometry] = useState(false);
  const [deleteNodeMode, setDeleteNodeMode] = useState(false);
  const deleteNodeModeRef = useRef(false);

  // DipDir form
  const [dip, setDip] = useState("");
  const [dipDir, setDipDir] = useState("");
  const [notes, setNotes] = useState("");

  // Weakness zone form (dip/dir/thickness optional)
  const [wzDip, setWzDip] = useState("");
  const [wzDipDir, setWzDipDir] = useState("");
  const [wzThickness, setWzThickness] = useState("");

  // Rock classification form
  const [rcRockType, setRcRockType] = useState("");
  const [rcRqd, setRcRqd] = useState("");
  // RMR sub-parameters
  const [rcRmrUcs, setRcRmrUcs] = useState("");
  const [rcRmrRqd, setRcRmrRqd] = useState("");
  const [rcRmrSpacing, setRcRmrSpacing] = useState("");
  const [rcRmrCondition, setRcRmrCondition] = useState("");
  const [rcRmrWater, setRcRmrWater] = useState("");
  const [rcRmrOrientation, setRcRmrOrientation] = useState("");
  // Q-system sub-parameters
  const [rcQRqd, setRcQRqd] = useState("");
  const [rcQJn, setRcQJn] = useState("");
  const [rcQJr, setRcQJr] = useState("");
  const [rcQJa, setRcQJa] = useState("");
  const [rcQJw, setRcQJw] = useState("");
  const [rcQSrf, setRcQSrf] = useState("");

  // Collapsible sections in rock class form
  const [rcShowRmr, setRcShowRmr] = useState(false);
  const [rcShowQ, setRcShowQ] = useState(false);

  // Computed values
  const rcRmrTotal = (() => {
    const vals = [rcRmrUcs, rcRmrRqd, rcRmrSpacing, rcRmrCondition, rcRmrWater, rcRmrOrientation].map(Number);
    if (vals.some(isNaN)) return null;
    if (!rcRmrUcs && !rcRmrRqd && !rcRmrSpacing && !rcRmrCondition && !rcRmrWater && !rcRmrOrientation) return null;
    return vals.reduce((a, b) => a + b, 0);
  })();

  const rcQTotal = (() => {
    const rqd = Number(rcQRqd); const jn = Number(rcQJn); const jr = Number(rcQJr);
    const ja = Number(rcQJa); const jw = Number(rcQJw); const srf = Number(rcQSrf);
    if (!rcQRqd || !rcQJn || !rcQJr || !rcQJa || !rcQJw || !rcQSrf) return null;
    if ([rqd, jn, jr, ja, jw, srf].some(isNaN) || jn === 0 || ja === 0 || srf === 0) return null;
    return (rqd / jn) * (jr / ja) * (jw / srf);
  })();

  // Photo attachments (shared across all tools during create/edit)
  const [pendingPhotos, setPendingPhotos] = useState<PhotoAttachment[]>([]);
  const [editPhotos, setEditPhotos] = useState<PhotoAttachment[]>([]);

  // Saved observations
  const [observations, setObservations] = useState<Observation[]>(() => loadObservations(project.id));

  /* ── Map init ────────────────────────────────────────────── */
  useEffect(() => {
    if (!mapRef.current || olMapRef.current) return;

    const baseLayers = createBaseLayers();

    // User location layer
    const positionFeature = new Feature();
    positionFeature.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#4285f4" }),
          stroke: new Stroke({ color: "#fff", width: 2.5 }),
        }),
      }),
    );
    const accuracyFeature = new Feature();
    const locationLayer = new VectorLayer({
      source: new VectorSource({ features: [accuracyFeature, positionFeature] }),
    });

    // Observations layer
    const obsLayer = new VectorLayer({ source: obsSourceRef.current });

    const view = new View({
      center: fromLonLat([10.0, 62.0]),
      zoom: 6,
      minZoom: 3,
      maxZoom: 18,
    });

    // Node editing layer (for weakness zone vertex editing)
    const nodeLayer = new VectorLayer({
      source: nodeSourceRef.current,
      style: nodeStyle,
      zIndex: 100,
    });
    nodeLayerRef.current = nodeLayer;

    // Tunnel centerline layer
    const tunnelSource = tunnelSourceRef.current;
    const tunnelLayer = new VectorLayer({
      source: tunnelSource,
      style: new Style({
        stroke: new Stroke({ color: "#ff6600", width: 4, lineDash: [12, 6] }),
      }),
      zIndex: 10,
    });
    tunnelLayerRef.current = tunnelLayer;

    // Fetch and display tunnel centerline
    fetch("/api/tunnel-centerline")
      .then((r) => r.text())
      .then((xml) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, "application/xml");
        const ns = "http://www.landxml.org/schema/LandXML-1.2";
        const coordGeom = doc.getElementsByTagNameNS(ns, "CoordGeom")[0];
        if (!coordGeom) return;

        // Extract all start/end points from Line elements
        const utmPoints: [number, number][] = [];
        for (const child of Array.from(coordGeom.children)) {
          const tag = child.localName;
          if (tag === "Line") {
            const startEl = child.getElementsByTagNameNS(ns, "Start")[0];
            const endEl = child.getElementsByTagNameNS(ns, "End")[0];
            const sParts = (startEl.textContent || "").trim().split(/\s+/).map(Number);
            const eParts = (endEl.textContent || "").trim().split(/\s+/).map(Number);
            if (utmPoints.length === 0) utmPoints.push([sParts[0], sParts[1]]);
            utmPoints.push([eParts[0], eParts[1]]);
          } else if (tag === "Curve") {
            const startEl = child.getElementsByTagNameNS(ns, "Start")[0];
            const endEl = child.getElementsByTagNameNS(ns, "End")[0];
            const sParts = (startEl.textContent || "").trim().split(/\s+/).map(Number);
            const eParts = (endEl.textContent || "").trim().split(/\s+/).map(Number);
            if (utmPoints.length === 0) utmPoints.push([sParts[0], sParts[1]]);
            utmPoints.push([eParts[0], eParts[1]]);
          }
        }

        const mapCoords = utmPoints.map(([e, n]) => fromLonLat(projectCoordsToLonLat(e, n)));
        const feature = new Feature({ geometry: new LineString(mapCoords) });
        feature.setId("tunnel-centerline");
        tunnelSource.addFeature(feature);

        // Zoom to tunnel extent if GPS hasn't centered yet
        const extent = feature.getGeometry()!.getExtent();
        view.fit(extent, { padding: [80, 80, 80, 80], maxZoom: 16, duration: 600 });
      })
      .catch((err) => console.warn("Failed to load tunnel centerline:", err));

    const map = new Map({
      target: mapRef.current,
      layers: [...baseLayers, tunnelLayer, locationLayer, obsLayer, nodeLayer],
      view,
      controls: [],
    });

    const geolocation = new Geolocation({
      trackingOptions: { enableHighAccuracy: true },
      projection: view.getProjection(),
      tracking: true,
    });

    let centeredOnUser = false;
    geolocation.on("change:position", () => {
      const coords = geolocation.getPosition();
      if (coords) {
        positionFeature.setGeometry(new Point(coords));
        if (!centeredOnUser) {
          centeredOnUser = true;
          view.animate({ center: coords, zoom: 15, duration: 600 });
        }
      }
    });
    geolocation.on("change:accuracyGeometry", () => {
      const geom = geolocation.getAccuracyGeometry();
      if (geom) {
        accuracyFeature.setGeometry(geom);
        accuracyFeature.setStyle(new Style({
          fill: new Fill({ color: "rgba(66,133,244,0.12)" }),
          stroke: new Stroke({ color: "rgba(66,133,244,0.35)", width: 1 }),
        }));
      }
    });
    geolocation.on("error", (e) => setLocError(e.message));

    olMapRef.current = map;
    geoRef.current = geolocation;

    // Click to select existing observations
    map.on("singleclick", (e) => {
      const feat = map.forEachFeatureAtPixel(e.pixel, (f) => f, {
        layerFilter: (l) => l === obsLayer,
        hitTolerance: 12,
      });
      if (feat && feat.getId()) {
        // Dispatch a custom event so React state can pick it up
        window.dispatchEvent(new CustomEvent("obs-select", { detail: feat.getId() }));
      }
    });

    // Restore persisted observations onto map
    for (const obs of loadObservations(project.id)) {
      addObsFeature(obs);
    }

    return () => {
      geolocation.setTracking(false);
      map.setTarget(undefined);
      olMapRef.current = null;
    };
  }, []);

  /* ── Layer visibility sync ──────────────────────────────── */
  useEffect(() => {
    tunnelLayerRef.current?.setVisible(showTunnel);
  }, [showTunnel]);

  const zoomToTunnel = useCallback(() => {
    const feat = tunnelSourceRef.current.getFeatureById("tunnel-centerline");
    if (!feat || !olMapRef.current) return;
    const extent = feat.getGeometry()!.getExtent();
    olMapRef.current.getView().fit(extent, { padding: [80, 80, 80, 80], maxZoom: 16, duration: 400 });
  }, []);

  /* ── Node editing helpers ────────────────────────────────── */

  /** Populate node layer with draggable point features for each vertex */
  const showNodes = useCallback((obsId: string) => {
    nodeSourceRef.current.clear();
    const feat = obsSourceRef.current.getFeatureById(obsId);
    if (!feat) return;
    const geom = feat.getGeometry() as LineString | undefined;
    if (!geom) return;

    const coords = geom.getCoordinates();
    coords.forEach((c, i) => {
      const nodeFeat = new Feature({ geometry: new Point(c) });
      nodeFeat.set("nodeIndex", i);
      nodeFeat.set("parentId", obsId);
      nodeFeat.setId(`node-${obsId}-${i}`);
      nodeSourceRef.current.addFeature(nodeFeat);
    });
  }, []);

  /** Clear all node features and remove translate interaction */
  const clearNodes = useCallback(() => {
    nodeSourceRef.current.clear();
    const map = olMapRef.current;
    if (map && translateRef.current) {
      map.removeInteraction(translateRef.current);
      translateRef.current = null;
    }
  }, []);

  /** Sync node positions back to the parent line geometry */
  const syncNodesToLine = useCallback((obsId: string) => {
    const lineFeat = obsSourceRef.current.getFeatureById(obsId);
    if (!lineFeat) return;

    // Collect nodes sorted by index
    const nodes: { index: number; coord: number[] }[] = [];
    nodeSourceRef.current.getFeatures().forEach((f) => {
      if (f.get("parentId") === obsId) {
        const pt = f.getGeometry() as Point;
        nodes.push({ index: f.get("nodeIndex"), coord: pt.getCoordinates() });
      }
    });
    nodes.sort((a, b) => a.index - b.index);

    if (nodes.length >= 2) {
      lineFeat.setGeometry(new LineString(nodes.map((n) => n.coord)));
    }
  }, []);

  /** Start translate interaction for node dragging */
  const activateNodeDrag = useCallback((obsId: string) => {
    const map = olMapRef.current;
    if (!map) return;

    if (translateRef.current) {
      map.removeInteraction(translateRef.current);
    }

    const translate = new Translate({
      layers: [nodeLayerRef.current!],
      hitTolerance: 14,
    });

    translate.on("translating", () => {
      syncNodesToLine(obsId);
    });
    translate.on("translateend", () => {
      syncNodesToLine(obsId);
    });

    map.addInteraction(translate);
    translateRef.current = translate;
  }, [syncNodesToLine]);

  // Listen for feature selection from map click
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent).detail as string;
      const obs = observations.find((o) => o.id === id);
      if (!obs) return;

      // Clean up previous node editing
      clearNodes();

      setSelectedId(id);
      if (obs.type === "dipdir") {
        setEditDip(String(obs.dip));
        setEditDipDir(String(obs.dipDirection));
        setEditNotes(obs.notes);
        setEditPhotos(obs.photos ?? []);
      } else if (obs.type === "weaknesszone") {
        setEditDip(obs.dip !== undefined ? String(obs.dip) : "");
        setEditDipDir(obs.dipDirection !== undefined ? String(obs.dipDirection) : "");
        setEditWzThickness(obs.thickness !== undefined ? String(obs.thickness) : "");
        setEditNotes(obs.notes);
        setEditPhotos(obs.photos ?? []);

        // Don't show nodes yet - wait for "Edit geometry" button
      } else if (obs.type === "rockclass") {
        setRcRockType(obs.rockType ?? "");
        setRcRqd(obs.rqd !== undefined ? String(obs.rqd) : "");
        setRcRmrUcs(obs.rmrUcs !== undefined ? String(obs.rmrUcs) : "");
        setRcRmrRqd(obs.rmrRqd !== undefined ? String(obs.rmrRqd) : "");
        setRcRmrSpacing(obs.rmrSpacing !== undefined ? String(obs.rmrSpacing) : "");
        setRcRmrCondition(obs.rmrCondition !== undefined ? String(obs.rmrCondition) : "");
        setRcRmrWater(obs.rmrWater !== undefined ? String(obs.rmrWater) : "");
        setRcRmrOrientation(obs.rmrOrientation !== undefined ? String(obs.rmrOrientation) : "");
        setRcQRqd(obs.qRqd !== undefined ? String(obs.qRqd) : "");
        setRcQJn(obs.qJn !== undefined ? String(obs.qJn) : "");
        setRcQJr(obs.qJr !== undefined ? String(obs.qJr) : "");
        setRcQJa(obs.qJa !== undefined ? String(obs.qJa) : "");
        setRcQJw(obs.qJw !== undefined ? String(obs.qJw) : "");
        setRcQSrf(obs.qSrf !== undefined ? String(obs.qSrf) : "");
        setRcShowRmr(false);
        setRcShowQ(false);
        setEditNotes(obs.notes);
        setEditPhotos(obs.photos ?? []);
      }
      setRepositioning(false);
      setDeleteNodeMode(false);
      deleteNodeModeRef.current = false;
    };
    window.addEventListener("obs-select", handler);
    return () => window.removeEventListener("obs-select", handler);
  }, [observations, clearNodes, showNodes, activateNodeDrag]);

  // Center map on selected feature and show selection highlight
  const prevSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    const map = olMapRef.current;
    const source = obsSourceRef.current;

    // Remove highlight from previously selected feature
    if (prevSelectedRef.current && prevSelectedRef.current !== selectedId) {
      const prevFeat = source.getFeatureById(prevSelectedRef.current);
      if (prevFeat) {
        prevFeat.set("_origStyle", undefined);
        // Restore original style
        const obs = observations.find((o) => o.id === prevSelectedRef.current);
        if (obs) {
          if (obs.type === "dipdir") {
            prevFeat.setStyle(new Style({
              image: new Icon({ src: dipDirIconSvg(obs.dip, obs.dipDirection), scale: 1, anchor: [0.5, 0.5] }),
            }));
          } else if (obs.type === "weaknesszone") {
            prevFeat.setStyle(weaknessZoneStyle);
          } else if (obs.type === "rockclass") {
            prevFeat.setStyle(new Style({
              image: new Icon({ src: rockClassIconSvg(), scale: 1, anchor: [0.5, 0.5] }),
            }));
          }
        }
      }
    }
    prevSelectedRef.current = selectedId;

    if (!selectedId || !map) return;

    const feat = source.getFeatureById(selectedId);
    if (!feat) return;

    // Center feature in the visible area above the bottom-sheet panel.
    // The panel can be up to 70dvh, so the visible map strip is roughly
    // the top 30% of the viewport.  We offset the map center downward
    // (in pixel space) so the feature lands in the middle of that strip.
    const geom = feat.getGeometry();
    if (geom) {
      const featureCoord = geom.getType() === "Point"
        ? (geom as Point).getCoordinates()
        : getCenter(geom.getExtent());
      const mapSize = map.getSize(); // [width, height] in px
      if (mapSize) {
        const panelFraction = 0.55; // approximate panel coverage (conservative)
        const visibleTop = mapSize[1] * (1 - panelFraction); // visible strip height
        const targetPixelY = visibleTop / 2; // center of visible strip
        const offsetPixels = (mapSize[1] / 2) - targetPixelY; // how far to shift down from true center
        const resolution = map.getView().getResolution() ?? 1;
        const offsetMapUnits = offsetPixels * resolution;
        map.getView().animate({
          center: [featureCoord[0], featureCoord[1] - offsetMapUnits],
          duration: 300,
        });
      } else {
        map.getView().animate({ center: featureCoord, duration: 300 });
      }
    }

    // Apply selection highlight (blue ring behind icon, or thicker stroke for lines)
    const obs = observations.find((o) => o.id === selectedId);
    if (obs) {
      if (obs.type === "dipdir") {
        feat.setStyle([
          new Style({ image: new CircleStyle({ radius: 22, fill: new Fill({ color: "rgba(33,150,243,0.25)" }), stroke: new Stroke({ color: "#2196f3", width: 2.5 }) }) }),
          new Style({ image: new Icon({ src: dipDirIconSvg(obs.dip, obs.dipDirection), scale: 1, anchor: [0.5, 0.5] }) }),
        ]);
      } else if (obs.type === "weaknesszone") {
        feat.setStyle([
          new Style({ stroke: new Stroke({ color: "rgba(33,150,243,0.4)", width: 10 }) }),
          weaknessZoneStyle,
        ]);
      } else if (obs.type === "rockclass") {
        feat.setStyle([
          new Style({ image: new CircleStyle({ radius: 22, fill: new Fill({ color: "rgba(33,150,243,0.25)" }), stroke: new Stroke({ color: "#2196f3", width: 2.5 }) }) }),
          new Style({ image: new Icon({ src: rockClassIconSvg(), scale: 1, anchor: [0.5, 0.5] }) }),
        ]);
      }
    }
  }, [selectedId, observations]);

  // Handle node deletion via tap in delete mode
  useEffect(() => {
    const map = olMapRef.current;
    if (!map || !selectedId) return;

    const key = map.on("singleclick", (e) => {
      if (!deleteNodeModeRef.current) return;

      const feat = map.forEachFeatureAtPixel(e.pixel, (f) => f, {
        layerFilter: (l) => l === nodeLayerRef.current,
        hitTolerance: 14,
      });

      if (feat && feat.get("parentId") === selectedId) {
        // Don't delete if only 2 nodes left
        const nodeCount = nodeSourceRef.current.getFeatures()
          .filter((f) => f.get("parentId") === selectedId).length;
        if (nodeCount <= 2) return;

        nodeSourceRef.current.removeFeature(feat as Feature);
        // Re-index remaining nodes
        const remaining = nodeSourceRef.current.getFeatures()
          .filter((f) => f.get("parentId") === selectedId);
        remaining.sort((a, b) => a.get("nodeIndex") - b.get("nodeIndex"));
        remaining.forEach((f, i) => f.set("nodeIndex", i));

        syncNodesToLine(selectedId);
      }
    });

    return () => {
      unByKey(key);
    };
  }, [selectedId, syncNodesToLine]);

  /* ── Add a feature to the obs layer for a given observation ── */
  const addObsFeature = useCallback((obs: Observation) => {
    if (obs.type === "dipdir") {
      const feat = new Feature({ geometry: new Point(fromLonLat([obs.lon, obs.lat])) });
      feat.setStyle(new Style({
        image: new Icon({
          src: dipDirIconSvg(obs.dip, obs.dipDirection),
          scale: 1,
          anchor: [0.5, 0.5],
        }),
      }));
      feat.setId(obs.id);
      obsSourceRef.current.addFeature(feat);
    } else if (obs.type === "weaknesszone") {
      const coords = obs.coords.map(([lon, lat]) => fromLonLat([lon, lat]));
      const feat = new Feature({ geometry: new LineString(coords) });
      feat.setStyle(weaknessZoneStyle);
      feat.setId(obs.id);
      obsSourceRef.current.addFeature(feat);
    } else if (obs.type === "rockclass") {
      const feat = new Feature({ geometry: new Point(fromLonLat([obs.lon, obs.lat])) });
      feat.setStyle(new Style({
        image: new Icon({
          src: rockClassIconSvg(),
          scale: 1,
          anchor: [0.5, 0.5],
        }),
      }));
      feat.setId(obs.id);
      obsSourceRef.current.addFeature(feat);
    }
  }, []);

  /* ── Actions ─────────────────────────────────────────────── */

  const goToUser = () => {
    const map = olMapRef.current;
    const coords = geoRef.current?.getPosition();
    if (map && coords) {
      map.getView().animate({ center: coords, zoom: 15, duration: 400 });
    }
  };

  const startPlacing = useCallback(() => {
    if (activeTool === "dipdir" || activeTool === "rockclass") {
      const map = olMapRef.current;
      const coords = geoRef.current?.getPosition();
      if (map && coords) {
        map.getView().animate({ center: coords, duration: 300 });
      }
      setPlacing(true);
      setDip("");
      setDipDir("");
      setNotes("");
      setPendingPhotos([]);
      if (activeTool === "rockclass") {
        setRcRockType("");
        setRcRqd("");
        setRcRmrUcs(""); setRcRmrRqd(""); setRcRmrSpacing(""); setRcRmrCondition(""); setRcRmrWater(""); setRcRmrOrientation("");
        setRcQRqd(""); setRcQJn(""); setRcQJr(""); setRcQJa(""); setRcQJw(""); setRcQSrf("");
        setRcShowRmr(false); setRcShowQ(false);
      }
    } else if (activeTool === "weaknesszone") {
      setPendingPhotos([]);
      startDrawing();
    }
  }, [activeTool]);

  /* ── Weakness zone drawing ───────────────────────────────── */

  const startDrawing = useCallback(() => {
    const map = olMapRef.current;
    if (!map) return;

    // Remove any existing draw interaction
    if (drawRef.current) {
      map.removeInteraction(drawRef.current);
    }

    const draw = new Draw({
      source: obsSourceRef.current,
      type: "LineString",
      style: weaknessZoneDrawStyle,
    });

    draw.on("drawend", (e) => {
      const geom = e.feature.getGeometry() as LineString;
      const mapCoords = geom.getCoordinates();
      const lonLatCoords = mapCoords.map((c) => toLonLat(c) as [number, number]);

      // Style the drawn feature
      e.feature.setStyle(weaknessZoneStyle);
      drawnFeatureRef.current = e.feature;

      setDrawnCoords(lonLatCoords);
      setDrawing(false);
      setNotes("");
      setWzDip("");
      setWzDipDir("");
      setWzThickness("");

      // Remove draw interaction
      map.removeInteraction(draw);
      drawRef.current = null;
    });

    map.addInteraction(draw);
    drawRef.current = draw;
    setDrawing(true);
    setDrawnCoords(null);
    drawnFeatureRef.current = null;
  }, []);

  const cancelDrawing = useCallback(() => {
    const map = olMapRef.current;
    if (map && drawRef.current) {
      map.removeInteraction(drawRef.current);
      drawRef.current = null;
    }
    // Remove the drawn-but-unsaved feature
    if (drawnFeatureRef.current) {
      obsSourceRef.current.removeFeature(drawnFeatureRef.current);
      drawnFeatureRef.current = null;
    }
    setDrawing(false);
    setDrawnCoords(null);
  }, []);

  const saveWeaknessZone = useCallback(() => {
    if (!drawnCoords || drawnCoords.length < 2) return;

    const dipVal = wzDip ? parseInt(wzDip, 10) : undefined;
    const dirVal = wzDipDir ? parseInt(wzDipDir, 10) : undefined;
    const thickVal = wzThickness ? parseFloat(wzThickness) : undefined;

    const obs: WeaknessZoneObservation = {
      id: crypto.randomUUID(),
      type: "weaknesszone",
      coords: drawnCoords,
      dip: (dipVal !== undefined && !isNaN(dipVal)) ? dipVal : undefined,
      dipDirection: (dirVal !== undefined && !isNaN(dirVal)) ? dirVal : undefined,
      thickness: (thickVal !== undefined && !isNaN(thickVal) && thickVal > 0) ? thickVal : undefined,
      notes: notes.trim(),
      photos: pendingPhotos.length > 0 ? pendingPhotos : undefined,
      timestamp: Date.now(),
    };

    // The feature is already on the source from drawend; just set its id
    if (drawnFeatureRef.current) {
      drawnFeatureRef.current.setId(obs.id);
      drawnFeatureRef.current = null;
    }

    setObservations((prev) => {
      const next = [...prev, obs];
      persistObservations(project.id, next);
      return next;
    });
    setDrawnCoords(null);
    setPendingPhotos([]);
  }, [drawnCoords, notes, wzDip, wzDipDir, wzThickness, pendingPhotos]);

  const cancelPlacing = useCallback(() => {
    setPlacing(false);
    setPlacingConfirmed(false);
  }, []);

  const saveObservation = useCallback(() => {
    const map = olMapRef.current;
    if (!map) return;

    const center = map.getView().getCenter();
    if (!center) return;
    const [lon, lat] = toLonLat(center);

    if (activeTool === "rockclass") {
      const obs: RockClassObservation = {
        id: crypto.randomUUID(),
        type: "rockclass",
        lon, lat,
        rockType: rcRockType.trim() || undefined,
        rqd: rcRqd ? parseInt(rcRqd, 10) : undefined,
        rmrUcs: rcRmrUcs ? Number(rcRmrUcs) : undefined,
        rmrRqd: rcRmrRqd ? Number(rcRmrRqd) : undefined,
        rmrSpacing: rcRmrSpacing ? Number(rcRmrSpacing) : undefined,
        rmrCondition: rcRmrCondition ? Number(rcRmrCondition) : undefined,
        rmrWater: rcRmrWater ? Number(rcRmrWater) : undefined,
        rmrOrientation: rcRmrOrientation ? Number(rcRmrOrientation) : undefined,
        rmr: rcRmrTotal ?? undefined,
        qRqd: rcQRqd ? Number(rcQRqd) : undefined,
        qJn: rcQJn ? Number(rcQJn) : undefined,
        qJr: rcQJr ? Number(rcQJr) : undefined,
        qJa: rcQJa ? Number(rcQJa) : undefined,
        qJw: rcQJw ? Number(rcQJw) : undefined,
        qSrf: rcQSrf ? Number(rcQSrf) : undefined,
        qValue: rcQTotal ?? undefined,
        notes: notes.trim(),
        photos: pendingPhotos.length > 0 ? pendingPhotos : undefined,
        timestamp: Date.now(),
      };

      addObsFeature(obs);
      setObservations((prev) => {
        const next = [...prev, obs];
        persistObservations(project.id, next);
        return next;
      });
      setPlacing(false);
      setPlacingConfirmed(false);
      setPendingPhotos([]);
      return;
    }

    const dipVal = parseInt(dip, 10);
    const dirVal = parseInt(dipDir, 10);
    if (isNaN(dipVal) || isNaN(dirVal) || dipVal < 0 || dipVal > 90 || dirVal < 0 || dirVal > 360) return;

    const obs: DipDirObservation = {
      id: crypto.randomUUID(),
      type: "dipdir",
      lon, lat,
      dip: dipVal,
      dipDirection: dirVal,
      notes: notes.trim(),
      photos: pendingPhotos.length > 0 ? pendingPhotos : undefined,
      timestamp: Date.now(),
    };

    addObsFeature(obs);

    setObservations((prev) => {
      const next = [...prev, obs];
      persistObservations(project.id, next);
      return next;
    });
    setPlacing(false);
    setPlacingConfirmed(false);
    setPendingPhotos([]);
  }, [activeTool, dip, dipDir, notes, rcRockType, rcRqd, rcRmrTotal, rcRmrUcs, rcRmrRqd, rcRmrSpacing, rcRmrCondition, rcRmrWater, rcRmrOrientation, rcQTotal, rcQRqd, rcQJn, rcQJr, rcQJa, rcQJw, rcQSrf, pendingPhotos, addObsFeature]);

  /* ── Edit / Delete ───────────────────────────────────────── */

  const selectedObs = selectedId ? observations.find((o) => o.id === selectedId) ?? null : null;

  const cancelEdit = useCallback(() => {
    // Clean up node editing
    clearNodes();

    // Revert weakness zone geometry if it was being edited
    if (selectedId) {
      const obs = observations.find((o) => o.id === selectedId);
      if (obs && obs.type === "weaknesszone") {
        const feat = obsSourceRef.current.getFeatureById(obs.id);
        if (feat) {
          const coords = obs.coords.map(([lon, lat]) => fromLonLat([lon, lat]));
          feat.setGeometry(new LineString(coords));
        }
      }
    }

    setSelectedId(null);
    setRepositioning(false);
    setEditingGeometry(false);
    setDeleteNodeMode(false);
    deleteNodeModeRef.current = false;
  }, [selectedId, observations, clearNodes]);

  const startReposition = useCallback(() => {
    if (!selectedObs || (selectedObs.type !== "dipdir" && selectedObs.type !== "rockclass")) return;
    const map = olMapRef.current;
    if (map) {
      map.getView().animate({ center: fromLonLat([selectedObs.lon, selectedObs.lat]), duration: 300 });
    }
    setRepositioning(true);
  }, [selectedObs]);

  const saveEdit = useCallback(() => {
    if (!selectedObs) return;

    setObservations((prev) => {
      const next = prev.map((o) => {
        if (o.id !== selectedObs.id) return o;

        if (o.type === "dipdir") {
          const dipVal = parseInt(editDip, 10);
          const dirVal = parseInt(editDipDir, 10);
          if (isNaN(dipVal) || isNaN(dirVal)) return o;

          let lon = o.lon;
          let lat = o.lat;
          if (repositioning) {
            const center = olMapRef.current?.getView().getCenter();
            if (center) {
              [lon, lat] = toLonLat(center);
            }
          }

          const updated: DipDirObservation = { ...o, dip: dipVal, dipDirection: dirVal, lon, lat, notes: editNotes.trim(), photos: editPhotos.length > 0 ? editPhotos : undefined };

          // Update map feature
          const feat = obsSourceRef.current.getFeatureById(o.id);
          if (feat) {
            feat.setGeometry(new Point(fromLonLat([updated.lon, updated.lat])));
            feat.setStyle(new Style({
              image: new Icon({
                src: dipDirIconSvg(updated.dip, updated.dipDirection),
                scale: 1,
                anchor: [0.5, 0.5],
              }),
            }));
          }
          return updated;
        }

        if (o.type === "weaknesszone") {
          const dipVal = editDip ? parseInt(editDip, 10) : undefined;
          const dirVal = editDipDir ? parseInt(editDipDir, 10) : undefined;
          const thickVal = editWzThickness ? parseFloat(editWzThickness) : undefined;

          // Read potentially modified geometry from the map feature
          let coords = o.coords;
          const feat = obsSourceRef.current.getFeatureById(o.id);
          if (feat) {
            const geom = feat.getGeometry() as LineString | undefined;
            if (geom) {
              coords = geom.getCoordinates().map((c) => toLonLat(c) as [number, number]);
            }
          }

          const updated: WeaknessZoneObservation = {
            ...o,
            coords,
            dip: (dipVal !== undefined && !isNaN(dipVal)) ? dipVal : undefined,
            dipDirection: (dirVal !== undefined && !isNaN(dirVal)) ? dirVal : undefined,
            thickness: (thickVal !== undefined && !isNaN(thickVal) && thickVal > 0) ? thickVal : undefined,
            notes: editNotes.trim(),
            photos: editPhotos.length > 0 ? editPhotos : undefined,
          };
          return updated;
        }

        if (o.type === "rockclass") {
          let lon = o.lon;
          let lat = o.lat;
          if (repositioning) {
            const center = olMapRef.current?.getView().getCenter();
            if (center) {
              [lon, lat] = toLonLat(center);
            }
          }

          const updated: RockClassObservation = {
            ...o,
            lon, lat,
            rockType: rcRockType.trim() || undefined,
            rqd: rcRqd ? parseInt(rcRqd, 10) : undefined,
            rmrUcs: rcRmrUcs ? Number(rcRmrUcs) : undefined,
            rmrRqd: rcRmrRqd ? Number(rcRmrRqd) : undefined,
            rmrSpacing: rcRmrSpacing ? Number(rcRmrSpacing) : undefined,
            rmrCondition: rcRmrCondition ? Number(rcRmrCondition) : undefined,
            rmrWater: rcRmrWater ? Number(rcRmrWater) : undefined,
            rmrOrientation: rcRmrOrientation ? Number(rcRmrOrientation) : undefined,
            rmr: rcRmrTotal ?? undefined,
            qRqd: rcQRqd ? Number(rcQRqd) : undefined,
            qJn: rcQJn ? Number(rcQJn) : undefined,
            qJr: rcQJr ? Number(rcQJr) : undefined,
            qJa: rcQJa ? Number(rcQJa) : undefined,
            qJw: rcQJw ? Number(rcQJw) : undefined,
            qSrf: rcQSrf ? Number(rcQSrf) : undefined,
            qValue: rcQTotal ?? undefined,
            notes: editNotes.trim(),
            photos: editPhotos.length > 0 ? editPhotos : undefined,
          };

          // Update map feature position if repositioned
          if (repositioning) {
            const feat = obsSourceRef.current.getFeatureById(o.id);
            if (feat) {
              feat.setGeometry(new Point(fromLonLat([updated.lon, updated.lat])));
            }
          }
          return updated;
        }

        return o;
      });
      persistObservations(project.id, next);
      return next;
    });

    // Clean up node editing
    clearNodes();

    setSelectedId(null);
    setRepositioning(false);
    setEditingGeometry(false);
    setDeleteNodeMode(false);
    deleteNodeModeRef.current = false;
    setEditPhotos([]);
  }, [selectedObs, editDip, editDipDir, editNotes, editWzThickness, repositioning, clearNodes, editPhotos, rcRockType, rcRqd, rcRmrTotal, rcRmrUcs, rcRmrRqd, rcRmrSpacing, rcRmrCondition, rcRmrWater, rcRmrOrientation, rcQTotal, rcQRqd, rcQJn, rcQJr, rcQJa, rcQJw, rcQSrf]);

  const deleteObs = useCallback(() => {
    if (!selectedObs) return;

    // Clean up modify interaction
    const map = olMapRef.current;
    if (map && modifyRef.current) {
      map.removeInteraction(modifyRef.current);
      modifyRef.current = null;
    }
    modifyFeaturesRef.current.clear();

    // Remove feature from map
    const feat = obsSourceRef.current.getFeatureById(selectedObs.id);
    if (feat) obsSourceRef.current.removeFeature(feat);

    setObservations((prev) => {
      const next = prev.filter((o) => o.id !== selectedObs.id);
      persistObservations(project.id, next);
      return next;
    });

    setSelectedId(null);
    setRepositioning(false);
  }, [selectedObs]);

  /* ── Derived state ───────────────────────────────────────── */

  // Are we in any active input mode?
  const inputActive = placing || drawing || drawnCoords !== null || selectedId !== null;

  /* ── Render ──────────────────────────────────────────────── */

  const TOOLS: { type: ToolType; icon: string; label: string; enabled: boolean }[] = [
    { type: "dipdir", icon: "\u{1F9ED}", label: "Dip/Dir", enabled: true },
    { type: "weaknesszone", icon: "\u{26A0}", label: "Weakness", enabled: true },
    { type: "rockclass", icon: "^^", label: "Rock Class", enabled: true },
  ];

  const btnStyle: React.CSSProperties = {
    width: 40, height: 40, borderRadius: 12,
    background: GLASS_PANEL, border: `1px solid ${PANEL_BORDER}`,
    color: "#f7f8fb", fontSize: "1.2rem", cursor: "pointer",
    display: "grid", placeItems: "center",
    backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    boxShadow: GLASS_SHADOW,
  };

  return (
    <div style={{ width: "100%", height: "100dvh", position: "relative", overflow: "hidden" }}>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

      {/* App toolbar button — top right */}
      <div style={{ position: "absolute", top: 12, right: 12, zIndex: 30 }}>
        <AppToolbarButton />
      </div>

      {/* ── Left column: title + sidebar (matching 3D Survey flex layout) ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, bottom: 80, zIndex: 30,
        display: "flex", flexDirection: "column",
        padding: "12px 0 0 12px",
        pointerEvents: "none",
        width: sidebarOpen ? "min(312px, calc(100vw - 76px))" : "auto",
        transition: "width 0.15s ease",
      }}>
        {/* Title bar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          flexShrink: 0,
        }}>
          <div style={{
            ...panelBase,
            pointerEvents: "auto",
            padding: isMobile ? "0.55rem 0.65rem" : "0.65rem 0.85rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.55rem",
            borderRadius: 16,
          }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                color: "#f7f8fb",
                cursor: "pointer",
                padding: "4px 8px",
                fontSize: "0.75rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: "0.92rem", lineHeight: 1.05, fontWeight: 650, letterSpacing: "-0.03em", color: "#f7f8fb" }}>RockRecord</h1>
              <div style={{ fontSize: "0.62rem", fontWeight: 500, color: "rgba(247,248,251,0.55)", marginTop: 1 }}>GIS Survey</div>
            </div>
          </div>

          {observations.length > 0 && !inputActive ? (
            <div style={{
              padding: "8px 12px", borderRadius: 10, ...panelBase,
              fontSize: "0.72rem", fontWeight: 600, flexShrink: 0,
              pointerEvents: "auto",
            }}>
              {observations.length} obs
            </div>
          ) : (
            <div style={{ width: isMobile ? 0 : 72, flexShrink: 0 }} />
          )}
        </div>

        {/* Sidebar */}
        <div style={{
          flex: 1, minHeight: 0,
          display: "flex", flexDirection: "column",
          position: "relative",
          marginTop: "0.4rem",
        }}>
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            style={{
              position: "absolute",
              top: "50%",
              right: sidebarOpen ? -28 : "auto",
              left: sidebarOpen ? "auto" : 0,
              transform: "translateY(-50%)",
              width: 28, height: 84,
              borderRadius: sidebarOpen ? "0 12px 12px 0" : 12,
              border: `1px solid ${PANEL_BORDER}`,
              borderLeft: sidebarOpen ? "none" : `1px solid ${PANEL_BORDER}`,
              background: GLASS_PANEL,
              backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
              color: HUD_TEXT,
              cursor: "pointer",
              pointerEvents: "auto",
              display: "grid", placeItems: "center",
              padding: 0, zIndex: 2,
            }}
          >
            <svg
              aria-hidden="true" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{
                transform: sidebarOpen ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.15s ease",
                opacity: 0.9,
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
              }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          {sidebarOpen && (
          <div className="hide-scrollbar" style={{
            ...sidebarCardStyle,
            pointerEvents: "auto",
            flex: 1, minHeight: 0,
            overflowY: "auto", overflowX: "hidden",
            padding: "0.45rem",
          }}>
            <div style={sidebarGridStyle}>
              <MapSidebarItem icon="TL" title="Tunnel centerline" subtitle="E16 alignment" active={showTunnel} activeColor="#ff6600" onClick={() => setShowTunnel((v) => !v)} onZoom={zoomToTunnel} />
            </div>
          </div>
          )}
        </div>
      </div>

      {/* ── Crosshair when placing dipdir ── */}
      {(placing || repositioning) && (
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none", zIndex: 20,
        }}>
          <svg width="48" height="48" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="4" fill="none" stroke="#ab2328" strokeWidth="2" />
            <line x1="24" y1="4" x2="24" y2="18" stroke="#ab2328" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="24" y1="30" x2="24" y2="44" stroke="#ab2328" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="4" y1="24" x2="18" y2="24" stroke="#ab2328" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="30" y1="24" x2="44" y2="24" stroke="#ab2328" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      )}

      {/* ── Zoom + locate (right edge, vertically centered) ── */}
      <div style={{
        position: "absolute", right: isMobile ? 10 : 20,
        top: "50%", transform: "translateY(-50%)",
        display: "flex", flexDirection: "column", gap: 6, zIndex: 10,
      }}>
        <button type="button" onClick={() => olMapRef.current?.getView().animate({ zoom: (olMapRef.current.getView().getZoom() ?? 6) + 1, duration: 200 })} style={btnStyle}>+</button>
        <button type="button" onClick={() => olMapRef.current?.getView().animate({ zoom: (olMapRef.current.getView().getZoom() ?? 6) - 1, duration: 200 })} style={btnStyle}>&minus;</button>
        <button type="button" onClick={goToUser} style={{ ...btnStyle, fontSize: "1rem" }} title="Go to my location">&#8982;</button>
      </div>

      {/* ── Bottom bar: add button + tool selector (idle state) ── */}
      {!inputActive && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0, zIndex: 10,
          padding: isMobile ? "0 12px 16px" : "0 24px 20px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        }}>
          <div style={{ display: "flex", gap: 6 }}>
            {TOOLS.map((t) => (
              <button
                key={t.type}
                type="button"
                disabled={!t.enabled}
                onClick={() => {
                  if (!t.enabled) return;
                  setActiveTool(t.type);
                  // auto-start placing after setting tool
                  if (t.type === "dipdir") {
                    const map = olMapRef.current;
                    const coords = geoRef.current?.getPosition();
                    if (map && coords) {
                      map.getView().animate({ center: coords, duration: 300 });
                    }
                    setPlacing(true);
                    setDip("");
                    setDipDir("");
                    setNotes("");
                    setPendingPhotos([]);
                  } else if (t.type === "weaknesszone") {
                    setPendingPhotos([]);
                    startDrawing();
                  } else if (t.type === "rockclass") {
                    const map = olMapRef.current;
                    const coords = geoRef.current?.getPosition();
                    if (map && coords) {
                      map.getView().animate({ center: coords, duration: 300 });
                    }
                    setPlacing(true);
                    setRcRockType("");
                    setRcRqd("");
                    setRcRmrUcs(""); setRcRmrRqd(""); setRcRmrSpacing(""); setRcRmrCondition(""); setRcRmrWater(""); setRcRmrOrientation("");
                    setRcQRqd(""); setRcQJn(""); setRcQJr(""); setRcQJa(""); setRcQJw(""); setRcQSrf("");
                    setRcShowRmr(false); setRcShowQ(false);
                    setNotes("");
                    setPendingPhotos([]);
                  }
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "10px 14px", borderRadius: 10,
                  ...panelBase,
                  color: t.enabled ? "#f7f8fb" : "rgba(247,248,251,0.3)",
                  cursor: t.enabled ? "pointer" : "default",
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                }}
              >
                <span>{t.type === "rockclass" ? (
                  <svg width="18" height="18" viewBox="0 0 32 32" style={{ verticalAlign: "middle" }}>
                    <polyline points="8,16 16,6 24,16" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="8,26 16,16 24,26" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : t.icon}</span>
                <span style={{ fontSize: "0.72rem", fontWeight: 600 }}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Drawing hint (while drawing weakness zone) ── */}
      {drawing && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0, zIndex: 20,
          padding: isMobile ? "12px 12px 16px" : "16px 24px 20px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        }}>
          <div style={{
            padding: "10px 20px", ...panelBase, borderRadius: 12,
            fontSize: "0.78rem", fontWeight: 600, textAlign: "center",
          }}>
            Tap points to draw weakness zone line. Double-tap to finish.
          </div>
          <button
            type="button"
            onClick={cancelDrawing}
            style={{
              padding: "10px 24px", borderRadius: 10,
              background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
              color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              ...panelBase,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Weakness zone notes panel (after drawing, before save) ── */}
      {drawnCoords !== null && !drawing && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          zIndex: 20,
          padding: isMobile ? "14px 12px 16px" : "18px 24px 20px",
          ...panelBase,
          borderRadius: "14px 14px 0 0",
          display: "flex", flexDirection: "column", gap: 10,
          maxWidth: 480, marginLeft: "auto", marginRight: "auto",
          maxHeight: "70dvh", overflowY: "auto",
        }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
            Weakness Zone
          </div>
          <div style={{ fontSize: "0.68rem", opacity: 0.45 }}>
            {drawnCoords.length} points drawn
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Dip (0-90)</label>
              <input
                type="number"
                inputMode="numeric"
                min={0} max={90}
                value={wzDip}
                onChange={(e) => setWzDip(e.target.value)}
                placeholder="optional"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Dip direction (0-360)</label>
              <input
                type="number"
                inputMode="numeric"
                min={0} max={360}
                value={wzDipDir}
                onChange={(e) => setWzDipDir(e.target.value)}
                placeholder="optional"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Thickness (m)</label>
            <input
              type="number"
              inputMode="decimal"
              min={0} step="0.1"
              value={wzThickness}
              onChange={(e) => setWzThickness(e.target.value)}
              placeholder="optional"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Description, infill, water..."
              rows={2}
              style={{ ...inputStyle, resize: "vertical", minHeight: 36 }}
            />
          </div>

          {/* Photo strip */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5 }}>Photos</label>
              <button type="button" onClick={() => capturePhoto((p) => { if (p) setPendingPhotos((prev) => [...prev, p]); })}
                style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(255,255,255,0.08)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", cursor: "pointer" }}>
                + Snap photo
              </button>
            </div>
            {pendingPhotos.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 6, overflowX: "auto" }}>
                {pendingPhotos.map((p, i) => (
                  <div key={p.id} style={{ position: "relative", flexShrink: 0 }}>
                    <img src={p.dataUrl} style={{ width: 52, height: 52, borderRadius: 6, objectFit: "cover" }} />
                    <button type="button" onClick={() => setPendingPhotos((prev) => prev.filter((_, j) => j !== i))}
                      style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: "#d32f2f", border: "none", color: "#fff", fontSize: "0.6rem", cursor: "pointer", display: "grid", placeItems: "center", lineHeight: 1 }}>x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={cancelDrawing}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
                color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              Discard
            </button>
            <button
              type="button"
              onClick={saveWeaknessZone}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: "#ab2328", border: "none",
                color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* ── DipDir: confirm position button (before form) ── */}
      {placing && !placingConfirmed && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0, zIndex: 10,
          padding: isMobile ? "0 12px 16px" : "0 24px 20px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        }}>
          <div style={{
            ...panelBase,
            borderRadius: "14px 14px 0 0",
            padding: isMobile ? "14px 12px 16px" : "18px 24px 20px",
            maxWidth: 480, marginLeft: "auto", marginRight: "auto",
            width: "100%",
            display: "flex", flexDirection: "column", gap: 10, alignItems: "center",
          }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
              Position crosshair
            </div>
            <div style={{ fontSize: "0.68rem", opacity: 0.45 }}>
              Drag map to place observation, then confirm
            </div>
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <button
                type="button"
                onClick={cancelPlacing}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10,
                  background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
                  color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setPlacingConfirmed(true)}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10,
                  background: "#1a8a4a", border: "none",
                  color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 4px 20px rgba(26,138,74,0.4)",
                }}
              >
                Confirm position
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DipDir input panel (after position confirmed) ── */}
      {placing && placingConfirmed && activeTool === "dipdir" && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          zIndex: 20,
          padding: isMobile ? "14px 12px 16px" : "18px 24px 20px",
          ...panelBase,
          borderRadius: "14px 14px 0 0",
          display: "flex", flexDirection: "column", gap: 10,
          maxWidth: 480, marginLeft: "auto", marginRight: "auto",
          maxHeight: "70dvh", overflowY: "auto",
        }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
            Dip / Dip Direction
          </div>
          <div style={{ fontSize: "0.68rem", opacity: 0.45 }}>
            Drag map to position crosshair, then fill in values
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Dip (0-90)</label>
              <input
                type="number"
                inputMode="numeric"
                min={0} max={90}
                value={dip}
                onChange={(e) => setDip(e.target.value)}
                placeholder="45"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Dip direction (0-360)</label>
              <input
                type="number"
                inputMode="numeric"
                min={0} max={360}
                value={dipDir}
                onChange={(e) => setDipDir(e.target.value)}
                placeholder="270"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Free text..."
              rows={2}
              style={{ ...inputStyle, resize: "vertical", minHeight: 36 }}
            />
          </div>

          {/* Photo strip */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5 }}>Photos</label>
              <button type="button" onClick={() => capturePhoto((p) => { if (p) setPendingPhotos((prev) => [...prev, p]); })}
                style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(255,255,255,0.08)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", cursor: "pointer" }}>
                + Snap photo
              </button>
            </div>
            {pendingPhotos.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 6, overflowX: "auto" }}>
                {pendingPhotos.map((p, i) => (
                  <div key={p.id} style={{ position: "relative", flexShrink: 0 }}>
                    <img src={p.dataUrl} style={{ width: 52, height: 52, borderRadius: 6, objectFit: "cover" }} />
                    <button type="button" onClick={() => setPendingPhotos((prev) => prev.filter((_, j) => j !== i))}
                      style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: "#d32f2f", border: "none", color: "#fff", fontSize: "0.6rem", cursor: "pointer", display: "grid", placeItems: "center", lineHeight: 1 }}>x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={cancelPlacing}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
                color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveObservation}
              disabled={!dip || !dipDir}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: (!dip || !dipDir) ? "rgba(171,35,40,0.3)" : "#ab2328",
                border: "none",
                color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: (!dip || !dipDir) ? "default" : "pointer",
                opacity: (!dip || !dipDir) ? 0.5 : 1,
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* ── Rock Classification input panel (after position confirmed) ── */}
      {placing && placingConfirmed && activeTool === "rockclass" && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          zIndex: 20,
          padding: isMobile ? "14px 12px 16px" : "18px 24px 20px",
          ...panelBase,
          borderRadius: "14px 14px 0 0",
          display: "flex", flexDirection: "column", gap: 10,
          maxWidth: 480, marginLeft: "auto", marginRight: "auto",
          maxHeight: "70dvh", overflowY: "auto",
        }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
            Rock Mass Classification
          </div>

          <div>
            <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Rock type</label>
            <input
              type="text"
              value={rcRockType}
              onChange={(e) => setRcRockType(e.target.value)}
              placeholder="e.g. Granite, Gneiss, Phyllite..."
              style={inputStyle}
            />
          </div>

          {/* RQD / RMR / Q on one line */}
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>RQD %</label>
              <input type="number" inputMode="numeric" min={0} max={100} value={rcRqd} onChange={(e) => setRcRqd(e.target.value)} placeholder="0-100" style={inputStyle} />
            </div>
            <button type="button" onClick={() => setRcShowRmr(!rcShowRmr)} style={{ flex: 1, padding: "8px 4px", borderRadius: 8, background: rcShowRmr ? "rgba(109,76,65,0.25)" : "rgba(255,255,255,0.04)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", fontWeight: 600, cursor: "pointer", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden" }}>
              RMR {rcRmrTotal !== null ? `${rcRmrTotal} (${rmrClassName(rcRmrTotal)})` : rcShowRmr ? "\u25B4" : "\u25BE"}
            </button>
            <button type="button" onClick={() => setRcShowQ(!rcShowQ)} style={{ flex: 1, padding: "8px 4px", borderRadius: 8, background: rcShowQ ? "rgba(109,76,65,0.25)" : "rgba(255,255,255,0.04)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", fontWeight: 600, cursor: "pointer", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden" }}>
              Q {rcQTotal !== null ? `${rcQTotal.toFixed(1)} (${qClassName(rcQTotal)})` : rcShowQ ? "\u25B4" : "\u25BE"}
            </button>
          </div>

          {/* ── RMR section (collapsible) ── */}
          {rcShowRmr && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${PANEL_BORDER}` }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, opacity: 0.5 }}>RMR (Bieniawski 1989)</div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>UCS — Intact rock strength</label>
                <select value={rcRmrUcs} onChange={(e) => setRcRmrUcs(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {RMR_UCS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>RQD rating</label>
                <select value={rcRmrRqd} onChange={(e) => setRcRmrRqd(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {RMR_RQD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Discontinuity spacing</label>
                <select value={rcRmrSpacing} onChange={(e) => setRcRmrSpacing(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {RMR_SPACING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Condition of discontinuities</label>
                <select value={rcRmrCondition} onChange={(e) => setRcRmrCondition(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {RMR_CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Groundwater</label>
                <select value={rcRmrWater} onChange={(e) => setRcRmrWater(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {RMR_WATER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Orientation adjustment</label>
                <select value={rcRmrOrientation} onChange={(e) => setRcRmrOrientation(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {RMR_ORIENTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {rcRmrTotal !== null && (
                <div style={{ fontSize: "0.72rem", fontWeight: 700, textAlign: "center", padding: "4px 0", color: rcRmrTotal >= 61 ? "#66bb6a" : rcRmrTotal >= 41 ? "#ffa726" : "#ef5350" }}>
                  RMR = {rcRmrTotal} &mdash; {rmrClassName(rcRmrTotal)}
                </div>
              )}
            </div>
          )}

          {/* ── Q-system section (collapsible) ── */}
          {rcShowQ && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${PANEL_BORDER}` }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, opacity: 0.5 }}>Q-system (Barton et al.)</div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>RQD (0-100)</label>
                <input type="number" inputMode="numeric" min={0} max={100} value={rcQRqd} onChange={(e) => setRcQRqd(e.target.value)} placeholder="0-100" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Jn &mdash; Joint set number</label>
                <select value={rcQJn} onChange={(e) => setRcQJn(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {Q_JN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Jr &mdash; Joint roughness number</label>
                <select value={rcQJr} onChange={(e) => setRcQJr(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {Q_JR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Ja &mdash; Joint alteration number</label>
                <select value={rcQJa} onChange={(e) => setRcQJa(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {Q_JA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Jw &mdash; Joint water reduction</label>
                <select value={rcQJw} onChange={(e) => setRcQJw(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {Q_JW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>SRF &mdash; Stress reduction factor</label>
                <select value={rcQSrf} onChange={(e) => setRcQSrf(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {Q_SRF_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {rcQTotal !== null && (
                <div style={{ fontSize: "0.72rem", fontWeight: 700, textAlign: "center", padding: "4px 0", color: rcQTotal >= 10 ? "#66bb6a" : rcQTotal >= 1 ? "#ffa726" : "#ef5350" }}>
                  Q = {rcQTotal.toFixed(3)} &mdash; {qClassName(rcQTotal)}
                </div>
              )}
            </div>
          )}

          <div>
            <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Comments</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Jointing, weathering, water conditions..."
              rows={2}
              style={{ ...inputStyle, resize: "vertical", minHeight: 36 }}
            />
          </div>

          {/* Photo strip */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5 }}>Photos</label>
              <button type="button" onClick={() => capturePhoto((p) => { if (p) setPendingPhotos((prev) => [...prev, p]); })}
                style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(255,255,255,0.08)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", cursor: "pointer" }}>
                + Snap photo
              </button>
            </div>
            {pendingPhotos.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 6, overflowX: "auto" }}>
                {pendingPhotos.map((p, i) => (
                  <div key={p.id} style={{ position: "relative", flexShrink: 0 }}>
                    <img src={p.dataUrl} style={{ width: 52, height: 52, borderRadius: 6, objectFit: "cover" }} />
                    <button type="button" onClick={() => setPendingPhotos((prev) => prev.filter((_, j) => j !== i))}
                      style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: "#d32f2f", border: "none", color: "#fff", fontSize: "0.6rem", cursor: "pointer", display: "grid", placeItems: "center", lineHeight: 1 }}>x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={cancelPlacing}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
                color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveObservation}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: "#ab2328",
                border: "none",
                color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* ── Geometry editing confirm bar (weakness zone) ── */}
      {selectedObs && editingGeometry && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0, zIndex: 20,
          padding: isMobile ? "0 12px 16px" : "0 24px 20px",
          display: "flex", flexDirection: "column", alignItems: "center",
        }}>
          <div style={{
            ...panelBase,
            borderRadius: "14px 14px 0 0",
            padding: isMobile ? "14px 12px 16px" : "18px 24px 20px",
            maxWidth: 480, marginLeft: "auto", marginRight: "auto",
            width: "100%",
            display: "flex", flexDirection: "column", gap: 10, alignItems: "center",
          }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
              Edit geometry
            </div>
            <div style={{ fontSize: "0.68rem", opacity: 0.45 }}>
              Drag nodes to reshape. Alt+click a node to delete it.
            </div>
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <button
                type="button"
                onClick={() => {
                  // Revert geometry
                  if (selectedObs && selectedObs.type === "weaknesszone") {
                    const feat = obsSourceRef.current.getFeatureById(selectedObs.id);
                    if (feat) {
                      const coords = selectedObs.coords.map(([lon, lat]) => fromLonLat([lon, lat]));
                      feat.setGeometry(new LineString(coords));
                    }
                  }
                  clearNodes();
                  setEditingGeometry(false);
                }}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10,
                  background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
                  color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  clearNodes();
                  setEditingGeometry(false);
                }}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10,
                  background: "#1a8a4a", border: "none",
                  color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 4px 20px rgba(26,138,74,0.4)",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit panel (when a feature is selected) ── */}
      {selectedObs && !editingGeometry && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          zIndex: 20,
          padding: isMobile ? "14px 12px 16px" : "18px 24px 20px",
          ...panelBase,
          borderRadius: "14px 14px 0 0",
          display: "flex", flexDirection: "column", gap: 10,
          maxWidth: 480, marginLeft: "auto", marginRight: "auto",
          maxHeight: "70dvh", overflowY: "auto",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
              Edit {selectedObs.type === "dipdir" ? "Dip/Dir" : selectedObs.type === "weaknesszone" ? "Weakness Zone" : "Rock Classification"}
            </div>
            <div style={{ fontSize: "0.6rem", opacity: 0.3, fontFamily: "monospace" }}>
              {new Date(selectedObs.timestamp).toLocaleString()}
            </div>
          </div>

          {/* Dip / Dir fields (required for dipdir, optional for wz) */}
          {(selectedObs.type === "dipdir" || selectedObs.type === "weaknesszone") && (
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>
                  Dip (0-90){selectedObs.type === "weaknesszone" ? " - optional" : ""}
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0} max={90}
                  value={editDip}
                  onChange={(e) => setEditDip(e.target.value)}
                  placeholder={selectedObs.type === "dipdir" ? "45" : "optional"}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>
                  Dip direction (0-360){selectedObs.type === "weaknesszone" ? " - optional" : ""}
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0} max={360}
                  value={editDipDir}
                  onChange={(e) => setEditDipDir(e.target.value)}
                  placeholder={selectedObs.type === "dipdir" ? "270" : "optional"}
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          {/* Thickness (weakness zone only) */}
          {selectedObs.type === "weaknesszone" && (
            <div>
              <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Thickness (m)</label>
              <input
                type="number"
                inputMode="decimal"
                min={0} step="0.1"
                value={editWzThickness}
                onChange={(e) => setEditWzThickness(e.target.value)}
                placeholder="optional"
                style={inputStyle}
              />
            </div>
          )}

          {/* Rock classification fields */}
          {selectedObs.type === "rockclass" && (
            <>
              <div>
                <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Rock type</label>
                <input type="text" value={rcRockType} onChange={(e) => setRcRockType(e.target.value)} placeholder="e.g. Granite, Gneiss, Phyllite..." style={inputStyle} />
              </div>

              {/* RQD / RMR / Q on one line */}
              <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>RQD %</label>
                  <input type="number" inputMode="numeric" min={0} max={100} value={rcRqd} onChange={(e) => setRcRqd(e.target.value)} placeholder="0-100" style={inputStyle} />
                </div>
                <button type="button" onClick={() => setRcShowRmr(!rcShowRmr)} style={{ flex: 1, padding: "8px 4px", borderRadius: 8, background: rcShowRmr ? "rgba(109,76,65,0.25)" : "rgba(255,255,255,0.04)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", fontWeight: 600, cursor: "pointer", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden" }}>
                  RMR {rcRmrTotal !== null ? `${rcRmrTotal} (${rmrClassName(rcRmrTotal)})` : rcShowRmr ? "\u25B4" : "\u25BE"}
                </button>
                <button type="button" onClick={() => setRcShowQ(!rcShowQ)} style={{ flex: 1, padding: "8px 4px", borderRadius: 8, background: rcShowQ ? "rgba(109,76,65,0.25)" : "rgba(255,255,255,0.04)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", fontWeight: 600, cursor: "pointer", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden" }}>
                  Q {rcQTotal !== null ? `${rcQTotal.toFixed(1)} (${qClassName(rcQTotal)})` : rcShowQ ? "\u25B4" : "\u25BE"}
                </button>
              </div>

              {/* RMR section */}
              {rcShowRmr && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${PANEL_BORDER}` }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 600, opacity: 0.5 }}>RMR (Bieniawski 1989)</div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>UCS — Intact rock strength</label>
                    <select value={rcRmrUcs} onChange={(e) => setRcRmrUcs(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {RMR_UCS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>RQD rating</label>
                    <select value={rcRmrRqd} onChange={(e) => setRcRmrRqd(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {RMR_RQD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Discontinuity spacing</label>
                    <select value={rcRmrSpacing} onChange={(e) => setRcRmrSpacing(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {RMR_SPACING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Condition of discontinuities</label>
                    <select value={rcRmrCondition} onChange={(e) => setRcRmrCondition(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {RMR_CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Groundwater</label>
                    <select value={rcRmrWater} onChange={(e) => setRcRmrWater(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {RMR_WATER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Orientation adjustment</label>
                    <select value={rcRmrOrientation} onChange={(e) => setRcRmrOrientation(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {RMR_ORIENTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {rcRmrTotal !== null && (
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, textAlign: "center", padding: "4px 0", color: rcRmrTotal >= 61 ? "#66bb6a" : rcRmrTotal >= 41 ? "#ffa726" : "#ef5350" }}>
                      RMR = {rcRmrTotal} &mdash; {rmrClassName(rcRmrTotal)}
                    </div>
                  )}
                </div>
              )}

              {/* Q-system section */}
              {rcShowQ && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${PANEL_BORDER}` }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 600, opacity: 0.5 }}>Q-system (Barton et al.)</div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>RQD (0-100)</label>
                    <input type="number" inputMode="numeric" min={0} max={100} value={rcQRqd} onChange={(e) => setRcQRqd(e.target.value)} placeholder="0-100" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Jn &mdash; Joint set number</label>
                    <select value={rcQJn} onChange={(e) => setRcQJn(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {Q_JN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Jr &mdash; Joint roughness number</label>
                    <select value={rcQJr} onChange={(e) => setRcQJr(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {Q_JR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Ja &mdash; Joint alteration number</label>
                    <select value={rcQJa} onChange={(e) => setRcQJa(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {Q_JA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>Jw &mdash; Joint water reduction</label>
                    <select value={rcQJw} onChange={(e) => setRcQJw(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {Q_JW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.62rem", opacity: 0.5, display: "block", marginBottom: 2 }}>SRF &mdash; Stress reduction factor</label>
                    <select value={rcQSrf} onChange={(e) => setRcQSrf(e.target.value)} style={selectStyle}>
                      <option value="">Select...</option>
                      {Q_SRF_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {rcQTotal !== null && (
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, textAlign: "center", padding: "4px 0", color: rcQTotal >= 10 ? "#66bb6a" : rcQTotal >= 1 ? "#ffa726" : "#ef5350" }}>
                      Q = {rcQTotal.toFixed(3)} &mdash; {qClassName(rcQTotal)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Edit geometry button (weakness zone only) */}
          {selectedObs.type === "weaknesszone" && (
            <button
              type="button"
              onClick={() => {
                setEditingGeometry(true);
                showNodes(selectedObs.id);
                activateNodeDrag(selectedObs.id);
              }}
              style={{
                padding: "8px 0", borderRadius: 8,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${PANEL_BORDER}`,
                color: "#f7f8fb", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
                textAlign: "center",
              }}
            >
              Edit geometry
            </button>
          )}

          {/* Reposition button (dipdir and rockclass) */}
          {(selectedObs.type === "dipdir" || selectedObs.type === "rockclass") && (
            <button
              type="button"
              onClick={startReposition}
              style={{
                padding: "8px 0", borderRadius: 8,
                background: repositioning ? "rgba(171,35,40,0.15)" : "rgba(255,255,255,0.04)",
                border: repositioning ? "1px solid rgba(171,35,40,0.4)" : `1px solid ${PANEL_BORDER}`,
                color: "#f7f8fb", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
                textAlign: "center",
              }}
            >
              {repositioning ? "Drag map to reposition, then Save" : "Reposition on map"}
            </button>
          )}

          <div>
            <label style={{ fontSize: "0.68rem", opacity: 0.5, display: "block", marginBottom: 3 }}>Notes</label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Free text..."
              rows={2}
              style={{ ...inputStyle, resize: "vertical", minHeight: 36 }}
            />
          </div>

          {/* Photo strip (edit mode) */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: "0.68rem", opacity: 0.5 }}>Photos</label>
              <button type="button" onClick={() => capturePhoto((p) => { if (p) setEditPhotos((prev) => [...prev, p]); })}
                style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(255,255,255,0.08)", border: `1px solid ${PANEL_BORDER}`, color: "#f7f8fb", fontSize: "0.68rem", cursor: "pointer" }}>
                + Snap photo
              </button>
            </div>
            {editPhotos.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 6, overflowX: "auto" }}>
                {editPhotos.map((p, i) => (
                  <div key={p.id} style={{ position: "relative", flexShrink: 0 }}>
                    <img src={p.dataUrl} style={{ width: 52, height: 52, borderRadius: 6, objectFit: "cover" }} />
                    <button type="button" onClick={() => setEditPhotos((prev) => prev.filter((_, j) => j !== i))}
                      style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: "#d32f2f", border: "none", color: "#fff", fontSize: "0.6rem", cursor: "pointer", display: "grid", placeItems: "center", lineHeight: 1 }}>x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={deleteObs}
              style={{
                padding: "10px 0", borderRadius: 10, width: 70,
                background: "rgba(211,47,47,0.15)", border: "1px solid rgba(211,47,47,0.4)",
                color: "#ff8a80", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              Delete
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: "rgba(255,255,255,0.06)", border: `1px solid ${PANEL_BORDER}`,
                color: "#f7f8fb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={selectedObs.type === "dipdir" && (!editDip || !editDipDir)}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: (selectedObs.type === "dipdir" && (!editDip || !editDipDir)) ? "rgba(171,35,40,0.3)" : "#ab2328",
                border: "none",
                color: "#fff", fontSize: "0.82rem", fontWeight: 700,
                cursor: (selectedObs.type === "dipdir" && (!editDip || !editDipDir)) ? "default" : "pointer",
                opacity: (selectedObs.type === "dipdir" && (!editDip || !editDipDir)) ? 0.5 : 1,
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Location error */}
      {locError && !inputActive && (
        <div style={{
          position: "absolute", bottom: isMobile ? 80 : 70, left: "50%", transform: "translateX(-50%)",
          padding: "6px 14px", borderRadius: 8, zIndex: 10,
          ...panelBase, color: "#ff8a80", fontSize: "0.72rem", fontWeight: 500,
          whiteSpace: "nowrap",
        }}>
          Location: {locError}
        </div>
      )}
    </div>
  );
}
